import {
  AppliedRaceEthnicityDto,
  RaceEthnicityDto,
  UpdateRaceEthnicityRequestDto,
  UserRaceEthnicityDto,
} from '@act/shared/data-transfer-objects';

/**
 * Applies previously reported user race and ethnicity selections to avaiable
 * race / ethnicity options to build form state.
 *
 * @param options - all race/ethnicity options
 * @param userEthnicity - reported user race/ethnicities
 *
 * @output subTrees - race/ethnicity options with reported options checked
 */
export const applyUserEthnicities = (
  options: RaceEthnicityDto[],
  userEthnicity: UserRaceEthnicityDto[]
): AppliedRaceEthnicityDto[] => {
  // Find all root options
  const roots = options.filter(option => !option.parentRaceEthnicityId);

  const subTrees: AppliedRaceEthnicityDto[] = roots.map(root => {
    // Check to see if reported user race / ethnicity includes this root
    const appliedRoot = userEthnicity.find(e => e.raceEthnicityId === root.id);

    // Check to see if this root has leaves
    const children = options.reduce((result: AppliedRaceEthnicityDto[], o) => {
      if (o.parentRaceEthnicityId === root.id) {
        // Check to see if reported user race / ethnicity includes this leaf
        const appliedLeaf = userEthnicity.find(et => {
          return et.raceEthnicityId === o.id;
        });

        // console.log('Applied Leaf', !!appliedLeaf, o.name);
        /**
         * If leaf does exist, push it into the child array
         *
         * @value name - name (read: label) for the identifier
         * @value raceEthnicityId - the associated table id for this leaf
         * @value detail - value of detail if leaf detail exists; null otherwise
         * @value value - true if user has reported; false otherwise
         */
        result.push({
          id: o.id,
          name: o.name,
          raceEthnicityId: o.id,
          requiresDetail: o.requiresDetail,
          detail: appliedLeaf?.detail ?? null,
          value: !!appliedLeaf,
        });
      }

      return result;
    }, []);

    /**
     * Create root with details.
     *
     * @value name - name (read: label) for the identifier
     * @value raceEthnicityId - the associated table id for this root
     * @value detail - value of detail if root detail exists; null otherwise
     * @value value - true if user has reported; false otherwise
     * @value children - if associated leaves exist; attach them here
     */
    const appliedSubTree: AppliedRaceEthnicityDto = {
      id: root.id,
      name: root.name,
      raceEthnicityId: root.id,
      requiresDetail: root.requiresDetail,
      detail: appliedRoot?.detail ?? null,
      value: !!appliedRoot,
    };

    if (children.length > 0) appliedSubTree.children = children;

    return appliedSubTree;
  });

  return subTrees;
};

/**
 * Converts previously appliped ethnicity array into an object to be used as form state.
 *
 * @param appliedEthnicities - all race/ethnicity options with user reported traits applied
 *
 * @output tree - race/ethnicity options converted to an object to be used as form state
 */
export const appliedEthnicitiesToObject = (
  appliedEthnicities: AppliedRaceEthnicityDto[]
) => {
  const tree = {};

  appliedEthnicities.forEach((e, i) => (tree[i] = e));

  return tree;
};

export const getChildKeys = (tree, subRoot) => {
  const nonChildKeys = ['detail', 'raceEthnicityId', 'requiresDetail', 'value'];

  return Object.keys(tree[subRoot]).filter(key => !nonChildKeys.includes(key));
};

export const packageRaceEthnicities = (tree: {
  [key: number]: AppliedRaceEthnicityDto;
}): UpdateRaceEthnicityRequestDto[] => {
  const persistPackage = [];
  const selectedOptions = Object.keys(tree).filter(key => tree[key].value);

  selectedOptions.forEach(key => {
    const selectedOption = tree[key];

    if (!selectedOption.requiresDetail || selectedOption.detail !== null) {
      persistPackage.push({
        raceEthnicityId: selectedOption.raceEthnicityId,
        detail: selectedOption.detail || null,
      });
    }

    const selectedChildOptions = selectedOption.children?.filter(child => {
      return child.value;
    });

    selectedChildOptions?.forEach(child => {
      if (!child.requiresDetail || child.detail !== null) {
        persistPackage.push({
          raceEthnicityId: child.raceEthnicityId,
          detail: child.detail || null,
        });
      }
    });
  });
  return persistPackage;
};

export const summarizeRawEthnicities = rawResponse => {
  const responseTree2D = [];
  const roots = rawResponse.filter(root => {
    return root.raceEthnicity.parentRaceEthnicityId === null;
  });

  roots.forEach(root => {
    const leaves = rawResponse.filter(leaf => {
      return leaf.raceEthnicity.parentRaceEthnicityId === root.raceEthnicityId;
    });

    responseTree2D.push([root, leaves]);
  });

  const summary = [];

  responseTree2D.forEach(subTree => {
    const root = subTree[0];
    const leaves = subTree[1];

    if (root.detail) summary.push(root.detail);
    if (!root.detail) {
      let subString = root.raceEthnicity.name;

      const leafNames = [];

      leaves.forEach(leaf => {
        if (leaf.detail) {
          leafNames.push(leaf.detail);
        } else {
          leafNames.push(leaf.raceEthnicity.name);
        }
      });

      if (leafNames.length > 0) {
        subString = subString + ` (${leafNames.join(', ')})`;
      }

      summary.push(subString);
    }
  });

  return summary.join(', ');
};

export const summarizeRaceEthnicities = tree => {
  const selections = [];

  const selectedKeys = Object.keys(tree).filter(key => tree[key].value);

  selectedKeys.forEach(key => {
    const selectedChildKeys = getChildKeys(tree, key).filter(
      childKey => tree[key][childKey].value
    );

    if (selectedChildKeys.length > 0) {
      const childSelections = selectedChildKeys.map(childKey => {
        if (tree[key][childKey].requireDetail) {
          return tree[key][childKey].detail;
        } else {
          return childKey;
        }
      });
      selections.push(`${key} (${childSelections.join(', ')})`);
    } else {
      selections.push(tree[key].requiresDetail ? tree[key].detail : key);
    }
  });

  return selections.join(', ');
};
